import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Camera, Mic, MicOff, Sparkles, Coins } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AgentMessage as AgentMessageType, AgentAction } from '@/hooks/useAuraAgent';
import { AgentMessage } from './AgentMessage';
import { useStyleCredits, CREDIT_COSTS } from '@/hooks/useStyleCredits';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface ChatInterfaceProps {
  messages: AgentMessageType[];
  isProcessing: boolean;
  onSendMessage: (message: string) => void;
  onAction: (action: AgentAction) => void;
  onCameraOpen: () => void;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onInsufficientCredits?: () => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  isProcessing,
  onSendMessage,
  onAction,
  onCameraOpen,
  messagesEndRef,
  onInsufficientCredits,
}) => {
  const [input, setInput] = useState('');
  const [isListening, setIsListening] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const { balance } = useStyleCredits();
  
  const creditCost = CREDIT_COSTS.agent_chat;
  const hasEnoughCredits = balance >= creditCost;

  // Initialize speech recognition
  useEffect(() => {
    const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognitionAPI) {
      recognitionRef.current = new SpeechRecognitionAPI();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInput(transcript);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Microphone access denied');
        }
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  const toggleVoiceInput = useCallback(() => {
    if (!recognitionRef.current) {
      toast.error('Voice input not supported in this browser');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setInput('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  }, [isListening]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isProcessing) return;
    
    // Check credits before sending
    if (!hasEnoughCredits) {
      onInsufficientCredits?.();
      return;
    }
    
    if (isListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsListening(false);
    }
    
    onSendMessage(input.trim());
    setInput('');
  };

  // Quick suggestions for new users
  const suggestions = [
    "What should I wear today?",
    "Show me my blue items",
    "Create a casual look",
    "Plan my week's outfits",
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 scrollbar-hide">
        {messages.map((message) => (
          <AgentMessage
            key={message.id}
            message={message}
            onAction={onAction}
            interactionId={message.interactionId}
          />
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick suggestions - show only if few messages */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2">
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            {suggestions.map((suggestion, idx) => (
              <motion.button
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                onClick={() => {
                  if (!hasEnoughCredits) {
                    onInsufficientCredits?.();
                    return;
                  }
                  onSendMessage(suggestion);
                }}
                className="flex-shrink-0 px-3 py-2 rounded-full bg-secondary text-secondary-foreground text-xs hover:bg-secondary/80 transition-colors"
              >
                {suggestion}
              </motion.button>
            ))}
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="p-4 border-t border-border bg-background/95 backdrop-blur-xl">
        {/* Credit Cost Indicator */}
        <div className="flex justify-center mb-2">
          <div className={cn(
            "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium",
            hasEnoughCredits 
              ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
              : "bg-red-500/10 text-red-500"
          )}>
            <Coins size={10} />
            <span>{creditCost} credits per message</span>
            {!hasEnoughCredits && <span className="ml-1">(need more)</span>}
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          {/* Camera button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onCameraOpen}
            className="flex-shrink-0 h-9 w-9 rounded-full hover:bg-aura-gold/10"
          >
            <Camera size={18} className="text-aura-gold" />
          </Button>

          {/* Text input with integrated voice button */}
          <div className="flex-1 relative flex items-center">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={isListening ? "Listening..." : "Ask AURA anything..."}
              disabled={isProcessing}
              className={cn(
                "pr-12 h-11 rounded-full bg-muted border-0 focus-visible:ring-1 focus-visible:ring-aura-gold transition-all",
                isListening && "ring-2 ring-aura-gold/50 bg-aura-gold/5"
              )}
            />
            {/* Voice input button inside input */}
            <button
              type="button"
              onClick={toggleVoiceInput}
              disabled={isProcessing}
              className={cn(
                "absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full transition-all",
                isListening 
                  ? "bg-aura-gold text-background animate-pulse" 
                  : "text-muted-foreground hover:text-aura-gold hover:bg-aura-gold/10"
              )}
            >
              <AnimatePresence mode="wait">
                {isListening ? (
                  <motion.div
                    key="mic-off"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                  >
                    <MicOff size={16} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="mic"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                  >
                    <Mic size={16} />
                  </motion.div>
                )}
              </AnimatePresence>
            </button>
          </div>

          {/* Send button */}
          <Button
            type="submit"
            size="icon"
            disabled={!input.trim() || isProcessing || !hasEnoughCredits}
            className={cn(
              "flex-shrink-0 h-9 w-9 rounded-full transition-all",
              input.trim() && hasEnoughCredits
                ? "bg-aura-gold text-background hover:bg-aura-gold/90" 
                : "bg-muted text-muted-foreground"
            )}
          >
            {isProcessing ? (
              <Sparkles size={16} className="animate-pulse" />
            ) : (
              <Send size={16} />
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};
